import { Component, Input, Output, OnChanges, EventEmitter, ElementRef, OnInit, OnDestroy } from '@angular/core';
import get from 'lodash-es/get';

import { CalendarService } from '@finder/shared/services/calendar-service/calendar-service.service';
import normalizeEventPartial from '@finder/shared/utils/object/normalizeEvent';
import { FeatureDetectionService } from '@finder/shared/services/feature-detection/feature-detection.service';
import { GetSimpleDatePipe } from './get-simple-date.pipe';
@Component({
    selector: 'finder-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss']
})
/**
 * Calendar Component to show a month calendar date picker.
 *
 * <finder-calendar
 *     [(date)]="date"
 *     [weekDayNameLength]="1"
 *     (dateSelected)="onDateSelected($event)">
 * </finder-calendar>
 */
export class CalendarComponent implements OnChanges, OnInit, OnDestroy {
    @Input() public labels: any = {};
    @Input() public date: Date = new Date();
    @Input() public weekDayNameLength = 1;
    @Input() public maxDays;
    @Input() public sync = true;
    @Input() public highlightDate = true;
    @Input() public allowNavigation = true;
    @Input() public displayYear = true;
    @Input() public dayData: any = {};
    @Input() highlightedDates: string[];
    @Output() public dateSelected = new EventEmitter<Date>();
    @Output() public calendarClose = new EventEmitter<KeyboardEvent>();
    private observer = new MutationObserver(() => this.checkDomChanges());

    calendarData: any;
    calendarObject: CalendarService;

    private selectFirstDay = false;
    private selectLastDay = false;
    private selectDay: any = null;
    private selectedText = ', Selected date';
    private delayFocus = false;

    constructor(
        public calendarService: CalendarService,
        private elementRef: ElementRef,
        private featureDetect: FeatureDetectionService,
        private getSimpleDatePipe: GetSimpleDatePipe
    ) {
        // This is our IE11 check
        this.delayFocus = !this.featureDetect.hasObjectFit();
    }

    ngOnInit(): void {
        // Listen for new calendar data through observable so the template redraws properly
        this.calendarObject.getCalendarDataObservable().subscribe(calendarData => {
            this.calendarData = calendarData;
        });

        this.observer.observe(this.elementRef.nativeElement, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    }

    ngOnDestroy(): void {
        this.observer.disconnect();
    }

    ngOnChanges() {
        /**
         * The default behavior of the calendar on a page is to stay in sync with each other
         * but in some cases we want the calendar to stand alone. This is used on the
         * availability calendar component.
         */
        this.calendarObject = this.sync ? this.calendarService : new CalendarService();

        // We need to recreate the calendar each time we pick a new day, so it focus on the new one
        // and re-draw the entirely month days. If not, there's an issue where the previous
        // selected month is being drawed instead the new one - Jira Ticket GIT-31030 -
        this.calendarObject.initCalendar(
            this.weekDayNameLength,
            this.date,
            this.maxDays,
            this.highlightedDates
        );
    }

    checkDomChanges() {
        if (this.selectFirstDay) {
            this.focusDayBySelector('.first-day');
            this.selectFirstDay = false;
        } else if (this.selectLastDay) {
            this.focusDayBySelector('.last-day');
            this.selectLastDay = false;
        } else if (this.selectDay) {
            if (!this.focusDay(this.selectDay.row, this.selectDay.column)) {
                if (this.selectDay.row === 0) {
                    this.focusDay(this.selectDay.row + 1, this.selectDay.column);
                } else {
                    this.focusDay(this.selectDay.row - 1, this.selectDay.column);
                }
            }
            this.selectDay = null;
        }
    }

    /**
     * Checking the data passed in to see if there is something we can use for a supplied day
     */
    getDayInData(day: number, prop: string): any {
        // Reuse the template pipe to get a simple date
        const simpleDate: string = this.getSimpleDatePipe.transform(this.calendarObject, day);

        return get(this.dayData, `${simpleDate}.${prop}`);
    }

    /**
     * Select a day in the Calendar Service
     * and emit and event with the date selected to the parent.
     *
     * @param day the day selected in the calendar.
     */
    selectDate(day, event) {
        this.cancelEvent(event);
        this.dateSelected.emit(this.calendarObject.selectCalendarDate(day));
    }

    isDaySelected(day, row, column) {
        // Don't auto select the date
        if (!this.highlightDate) {
            return false;
        }

        return this.calendarObject.isSelectedDate(day);
    }

    isHighlightedDate(day) {
        if (!day) {
            return false;
        }

        return this.calendarObject.isHighlightedDate(day);
    }

    focusCurrentDay(event) {
        this.cancelEvent(event);

        if (!this.focusDayBySelector('.calendar-cell.selected a')) {
            this.focusDayBySelector('.first-day');
        }
    }

    moveUp(row, column, event) {
        this.cancelEvent(event);

        // when going up far from first row move to prev month
        // unless we are in current month.
        if (!this.focusDay(row, column) && this.allowNavigation && !this.calendarObject.isCurrentMonth()) {
            row = this.calendarObject.getWeeksCount() - 1;
            this.selectDay = {row, column};
            this.calendarObject.previousMonth();
        }
    }

    moveDown(row, column, event) {
        this.cancelEvent(event);

        // if move down far from last row
        // move to next month.
        if (!this.focusDay(row, column)) {
            row = 0;
            if (this.calendarObject.isNextMonthEnabled() && this.allowNavigation) {
                this.selectDay = {row, column};
                this.calendarObject.nextMonth();
            }
        }
    }

    movePrevious(row, column, event) {
        this.cancelEvent(event);

        // if we are in first element in a row that is not the first one
        // move to the previous row.
        if (column < 0 && row > 0) {
            column = this.calendarData[0].length - 1;
            row = row - 1;
        }

        // if moved prev to first element in the calendar then go to prev month,
        // unles we are in the current month.
        if (!this.focusDay(row, column) && row === 0 && this.allowNavigation && !this.calendarObject.isCurrentMonth()) {
            this.selectLastDay = true;
            this.calendarObject.previousMonth();
        }
    }

    moveNext(row, column, event) {
        this.cancelEvent(event);
        const weeks = this.calendarObject.getWeeksCount();

        // if move next to the following row
        if (column >= this.calendarData[0].length && row < weeks - 1) {
            column = 0;
            row = row + 1;
        }

        // if moved to last element in the calendar then go to next month.
        if (!this.focusDay(row, column) && row >= weeks - 1 && this.allowNavigation && this.calendarObject.isNextMonthEnabled()) {
            this.selectFirstDay = true;
            this.calendarObject.nextMonth();
        }
    }

    focusElement(element) {
        // IE11 has to wait for the page to settle before rendering
        if (this.delayFocus) {
            setTimeout(() => element.focus(), 0);
        } else {
            element.focus();
        }
    }

    focusDay(row, column): boolean {
        if (this.calendarData[row] && this.calendarData[row][column]) {
            return this.focusDayBySelector(`.cell-${row}-${column}`);
        }

        return false;
    }

    focusDayBySelector(selector): boolean {
        let isFocused = false;
        const cellElement = this.elementRef.nativeElement.querySelector(selector);
        if (cellElement) {
            this.focusElement(cellElement);
            isFocused = true;
        }

        return isFocused;
    }

    tabAction(event: KeyboardEvent) {
        // It only will change its default propagation if it comes from a date cell
        // or it comes from the next arrow when prev arrow is enabled
        const eventTarget = event.target as Element;
        if (!eventTarget.classList.contains('arrow-next') || this.calendarObject.isPreviousMonthEnabled()) {
            this.cancelEvent(event);
            const nextTabIndexSelector = (this.calendarObject.isNextMonthEnabled() &&
                !eventTarget.classList.contains('arrow-next'))
                ? '.arrow-next'
                : '.arrow-prev';
            this.focusDayBySelector(nextTabIndexSelector);
        }
    }

    close(event) {
        this.calendarClose.emit(event);
    }

    nextArrowKey(row, column, event) {
        this.moveNext(row, column + 1, event);
    }

    previousArrowKey(row, column, event) {
        this.movePrevious(row, column - 1, event);
    }

    upArrowKey(row, column, event) {
        this.moveUp(row - 1, column, event);
    }

    downArrowKey(row, column, event) {
        this.moveDown(row + 1, column, event);
    }

    getCellDayText(day, column, isSelected) {
        const calendar = this.calendarObject;

        return `${calendar.fullNameWeekDays[column]}, ${calendar.selectedMonth} ${day}, ${
            calendar.selectedYear}${isSelected ? this.selectedText : ''}`;
    }

    onCalendarKeydown(event) {
        // IE11 doesn't properly prefix arrow events
        const partialEvent = normalizeEventPartial(event);

        switch (partialEvent.key) {
            case 'ArrowUp':
                this.focusCurrentDay(event);
                break;
            case 'ArrowDown':
                this.focusCurrentDay(event);
                break;
            case 'ArrowRight':
                this.focusCurrentDay(event);
                break;
            case 'ArrowLeft':
                this.focusCurrentDay(event);
                break;
            case 'Escape':
                this.close(event);
                break;
        }
    }

    onKeydown(data, event) {
        // IE11 doesn't properly prefix arrow events
        const partialEvent = normalizeEventPartial(event);

        switch (partialEvent.key) {
            case 'ArrowUp':
                this.upArrowKey(data.row, data.column, event);
                break;
            case 'ArrowDown':
                this.downArrowKey(data.row, data.column, event);
                break;
            case 'ArrowRight':
                this.nextArrowKey(data.row, data.column, event);
                break;
            case 'ArrowLeft':
                this.previousArrowKey(data.row, data.column, event);
                break;
            case 'Enter':
                this.selectDate(data.day, event);
                break;
            case 'Escape':
                this.close(event);
                break;
            case 'Tab':
                this.tabAction(event);
                break;
        }
    }

    cancelEvent(event) {
        event.preventDefault();
        event.stopPropagation();
    }
}
